import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import LoadingCircle from 'components/ui/LoadingCircle';
import Pagination from 'components/v2/Pagination';
import { Link } from 'react-router-dom';
import { orderBy, groupBy } from 'lodash';

import {
  getShipperPerformanceMetrics,
  getCargomaticPerformanceMetrics,
} from 'utils/MetricAPIUtils';

const PER_PAGE = 10;

const TableColumnHeader = (props) => {
  const { label } = props;
  const tableHeaderClassNames = classnames({
    'px-6': 1,
    'py-3': 1,
    'text-left': 1,
    'text-xs': 1,
    'font-medium': 1,
    'text-white': 1,
    uppercase: 1,
    'tracking-wider': 1,
  });
  return (
    <th scope="col" className={tableHeaderClassNames}>
      {label}
    </th>
  );
};

const MetricPercentageCell = (props) => {
  const { value } = props;
  return (
    <td className="px-6 py-4 whitespace-nowrap">
      {(value || value >= 0) && (
        <span className="text-grey-darkest">{value}%</span>
      )}
      {Number.isNaN(value) && (
        <div className="flex items-center">
          <LoadingCircle text="" color="green" />
        </div>
      )}
    </td>
  );
};

const UserRow = (props) => {
  const { user, type } = props;
  const getUserLink = () => {
    const url =
      type === 'Carrier'
        ? `/carrier-metrics/${user._id}`
        : `/metrics/${user._id}`;
    return (
      <Link target="_blank" rel="noopener noreferrer" to={url}>
        {user.name}
      </Link>
    );
  };

  return (
    <tr>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900">
              {getUserLink()}
            </div>
          </div>
        </div>
      </td>
      <MetricPercentageCell
        value={((user.overallStars / 5) * 100).toFixed(2)}
      />
    </tr>
  );
};

const MetricList = ({ id, type, dateFilters, shipmentType }) => {
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [users, setUsers] = useState([]);

  function handlePageClick(selectedPage) {
    setPage(selectedPage);
  }

  useEffect(() => {
    const getMetrics = async () => {
      let query = {
        ...dateFilters,
        page,
        paginate: true,
        type,
      };
      if (shipmentType) {
        query.shipmentType = shipmentType;
      }
      let results = {};
      let groupedData = [];
      let data = [];

      if (id) {
        query = { ...query, shipper: id };
        results = await getShipperPerformanceMetrics(query);
        data = results.items;
        setPages(results.pages);

        groupedData = groupBy(data, (metric) => metric.carrier._id);
      } else {
        query = { ...query, type };
        data = results.items;
        results = await getCargomaticPerformanceMetrics(query);
        data = results.items;
        setPages(results.pages);

        groupedData = groupBy(data, (user) => user.account._id);
      }

      if (data.length === 0) {
        return;
      }

      const stats = Object.keys(groupedData).reduce((statItems, userId) => {
        const items = groupedData[userId];
        const totalScore = items.reduce((sum, metric) => {
          return sum + (metric.overallStars || 0);
        }, 0);

        const averageScore = totalScore / items.length;

        const firstMetric = items[0];
        const targetId = id ? firstMetric.carrier._id : firstMetric.account._id;

        const name =
          type === 'Shipper'
            ? firstMetric.account.company_name
            : firstMetric.carrier.name;

        return statItems.concat({
          timestamp: data[0].timestamp,
          overallStars: averageScore,
          _id: targetId,
          account: targetId,
          _type: firstMetric._type,
          name,
        });
      }, []);

      setUsers(orderBy(stats, ['overallStars'], ['desc']));
    };
    getMetrics();
  }, [type, dateFilters, shipmentType, page]);

  return (
    <div className="flex flex-col">
      <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
          <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-700">
                <tr>
                  <TableColumnHeader label="Name" />
                  <TableColumnHeader label="Overall" />
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(!users || users.length === 0) && (
                  <tr>
                    <td colSpan={6} className="p-6">
                      <span className="text-sm text-gray-900">
                        No data at the moment...
                      </span>
                    </td>
                  </tr>
                )}
                {users &&
                  users.length > 0 &&
                  users.map((d) => {
                    return (
                      <UserRow
                        key={d._id}
                        user={d}
                        dateFilters={dateFilters}
                        type={type}
                      />
                    );
                  })}
              </tbody>
            </table>
            <Pagination
              className=""
              key={1}
              perPage={PER_PAGE}
              page={page}
              pages={pages}
              total={total}
              onPageClick={handlePageClick}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetricList;
